// Compress images before they leave the device.
//
// Two problems, one fix. A modern phone camera shoots ~50MP JPEGs (8160×6120,
// 10–20MB on a Galaxy A54): uploading one takes ~30s on mobile data — long enough
// for a flaky connection to drop the direct-to-Azure PUT — and rendering it costs a
// ~190MB decoded bitmap, past the per-tab memory budget on mid-range Android, which
// kills the tab with no error. But plenty of merely *heavy* images have no huge
// dimension at all: a 2000px photo off a DSLR, or a screenshot, can still be several
// MB of needless upload.
//
// So the trigger is either axis — oversized pixels OR oversized bytes:
//
//   - longest edge > MAX_EDGE       → resample down to MAX_EDGE
//   - bytes > COMPRESS_ABOVE_BYTES  → re-encode as JPEG, stepping down the quality
//                                     ladder until it fits TARGET_BYTES
//
// The backend already applies the equivalent policy to HEIC (transcode + 4000px cap
// in services/files.js); this covers the JPEG/PNG everything else uploads.
//
// Every failure path returns the ORIGINAL file, and so does any result that comes
// out bigger than what we started with. This must never be the reason an upload
// that works today stops working.

// Generous enough that a document photo stays sharp when zoomed; the server caps
// its own HEIC transcode at 4000.
const MAX_EDGE = 2500;

// Anything heavier than this is worth re-encoding even when its dimensions are
// already fine. Below it, the saving isn't worth the quality cost.
const COMPRESS_ABOVE_BYTES = 500 * 1024;

// What we aim to land under. The first quality that achieves it wins, so most
// images are encoded exactly once, at the top of the ladder.
const TARGET_BYTES = 1024 * 1024;
const QUALITY_LADDER = [0.85, 0.75, 0.65];

// Enough to reach the SOF segment past any EXIF/ICC payload a camera prepends.
const HEADER_BYTES = 256 * 1024;

// Only formats we can both measure from the header and decode reliably. HEIC/HEIF
// are deliberately absent — browsers can't decode them, and the server transcodes
// them anyway. PDFs and SVGs are not images to resample.
const DOWNSCALABLE = new Set(['image/jpeg', 'image/png']);

interface Dimensions {
  width: number;
  height: number;
}

function devLog(message: string, detail: Record<string, unknown>): void {
  if (!import.meta.env.DEV) return;
  // eslint-disable-next-line no-console
  console.info(`[upload] ${message}`, detail);
}

// Read the dimensions out of the header rather than decoding the image: a full
// decode is the very allocation this module exists to avoid.
function readJpegSize(view: DataView): Dimensions | null {
  if (view.byteLength < 4 || view.getUint16(0) !== 0xffd8) return null;

  let offset = 2;
  while (offset + 4 <= view.byteLength) {
    if (view.getUint8(offset) !== 0xff) {
      offset += 1;
      continue;
    }
    const marker = view.getUint8(offset + 1);
    offset += 2;

    // Standalone markers carry no length field.
    if (marker === 0x01 || (marker >= 0xd0 && marker <= 0xd8)) continue;
    // Start of scan / end of image — pixel data begins, no SOF was found.
    if (marker === 0xda || marker === 0xd9) return null;

    const length = view.getUint16(offset);
    const isSof =
      marker >= 0xc0 && marker <= 0xcf && marker !== 0xc4 && marker !== 0xc8 && marker !== 0xcc;
    if (isSof) {
      // Segment body: length(2) precision(1) height(2) width(2)
      if (offset + 7 > view.byteLength) return null;
      return { height: view.getUint16(offset + 3), width: view.getUint16(offset + 5) };
    }
    if (length < 2) return null;
    offset += length;
  }
  return null;
}

function readPngSize(view: DataView): Dimensions | null {
  if (view.byteLength < 24) return null;
  if (view.getUint32(0) !== 0x89504e47 || view.getUint32(4) !== 0x0d0a1a0a) return null;
  // IHDR is always the first chunk: width and height lead its body.
  return { width: view.getUint32(16), height: view.getUint32(20) };
}

async function readIntrinsicSize(file: File): Promise<Dimensions | null> {
  const buffer = await file.slice(0, HEADER_BYTES).arrayBuffer();
  const view = new DataView(buffer);
  const size = file.type === 'image/png' ? readPngSize(view) : readJpegSize(view);
  if (!size || !size.width || !size.height) return null;
  return size;
}

function encodeJpeg(canvas: HTMLCanvasElement, quality: number): Promise<Blob | null> {
  return new Promise((resolve) => {
    canvas.toBlob(resolve, 'image/jpeg', quality);
  });
}

function drawToCanvas(bitmap: ImageBitmap): HTMLCanvasElement | null {
  const canvas = document.createElement('canvas');
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  // Transparent PNG pixels flatten to black on JPEG; white suits the document
  // previews these end up in.
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(bitmap, 0, 0);
  return canvas;
}

function toJpegName(name: string): string {
  const base = name.replace(/\.[^./\\]+$/, '');
  return `${base || 'photo'}.jpg`;
}

export async function downscaleImage(file: File): Promise<File> {
  if (!file || !DOWNSCALABLE.has(file.type)) return file;
  if (typeof createImageBitmap !== 'function') return file;

  try {
    const size = await readIntrinsicSize(file);
    if (!size) {
      // Without dimensions we can't bound the decode, and decoding blind is the
      // memory blow-up this module exists to prevent.
      devLog('skipped: could not read dimensions', { name: file.name, bytes: file.size });
      return file;
    }

    const needsResize = Math.max(size.width, size.height) > MAX_EDGE;
    const needsCompress = file.size > COMPRESS_ABOVE_BYTES;
    if (!needsResize && !needsCompress) {
      devLog('skipped: already small', {
        name: file.name,
        bytes: file.size,
        dimensions: `${size.width}×${size.height}`,
      });
      return file;
    }

    // Constrain ONE dimension and let the browser derive the other so the aspect
    // ratio survives untouched. Passing both would distort any photo whose EXIF
    // orientation transposes the header's width/height — common on phone cameras,
    // where the sensor writes landscape pixels plus a "rotate 90°" tag.
    let resize = {};
    if (needsResize) {
      resize = size.width >= size.height ? { resizeWidth: MAX_EDGE } : { resizeHeight: MAX_EDGE };
    }

    const bitmap = await createImageBitmap(file, {
      ...resize,
      resizeQuality: 'high',
      imageOrientation: 'from-image',
    });

    let blob: Blob | null = null;
    let usedQuality = 0;
    const finalWidth = bitmap.width;
    const finalHeight = bitmap.height;
    try {
      const canvas = drawToCanvas(bitmap);
      if (!canvas) return file;
      // Stop at the first quality that meets the target; only genuinely heavy
      // images ever pay for a second or third encode.
      for (let i = 0; i < QUALITY_LADDER.length; i += 1) {
        usedQuality = QUALITY_LADDER[i];
        // eslint-disable-next-line no-await-in-loop
        blob = await encodeJpeg(canvas, usedQuality);
        if (!blob || blob.size <= TARGET_BYTES) break;
      }
    } finally {
      bitmap.close();
    }

    // An already-efficient original can beat our re-encode; keep the smaller one.
    if (!blob || blob.size >= file.size) {
      devLog('skipped: re-encode was no smaller', {
        name: file.name,
        bytes: file.size,
        wouldBe: blob?.size,
      });
      return file;
    }

    devLog('compressed', {
      name: file.name,
      from: `${(file.size / 1024 / 1024).toFixed(2)}MB ${size.width}×${size.height}`,
      to: `${(blob.size / 1024 / 1024).toFixed(2)}MB ${finalWidth}×${finalHeight}`,
      quality: usedQuality,
      saved: `${Math.round((1 - blob.size / file.size) * 100)}%`,
    });

    return new File([blob], toJpegName(file.name), {
      type: 'image/jpeg',
      lastModified: file.lastModified,
    });
  } catch {
    return file;
  }
}

export default downscaleImage;
